import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import Dropdown from "@cayuse/shared-react.components.dropdown";
import Checkbox from "@cayuse/shared-react.components.form.inputs.checkbox";
import IconButton from "@cayuse/shared-react.components.icon-button";
import { crunchClasses } from "@cayuse/shared-react.utils.helpers";
import { isNonEmptyArray, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import { EditableTableCell } from "components/BudgetBuilder/shared/EditableTableCell";
import { useTenantFormatter } from "components/BudgetBuilder/shared/TenantFormatter";
import Table from "components/Table";
import UnitSearchInput from "components/UnitSearchInput";
import { REQUIREMENT_DROPDOWN_ITEMS, TYPE_DROPDOWN_OPTIONS } from "globals/constants";
import { budgetBuilderSelectors } from "store/selectors";
import { fetchSingleUnit } from "store/actions/picklists";
import { picklistsSelectors, tenantSelectors } from "store/selectors";
import {
	convertCategoriesToOptions,
	convertOptionsToTextByValue,
	formatNumberAsPercent,
	isPresent,
	toNumber,
} from "utils/budgetBuilder";

import styles from "./styles.css";


const InternalCostShareRow = (
	{
		category,
		isEditing,
		handleSyncRemoveItem,
		handleUpdateInternalLineItem
	}
) => {
	let {
		hash,
		value,
		requirement,
		unit,
		type,
		costShareAmount,
		isFA,
	} = category;
	const faRateAmount = useSelector(budgetBuilderSelectors.currentPeriodRate);
	const categories = useSelector(tenantSelectors.proposalCategories);
	const { options: units } = useSelector(picklistsSelectors.units);

	const { currencyFormatter } = useTenantFormatter();

	useEffect(() => {
		if (isNonEmptyString(unit) && isNonEmptyArray(units)) {
			if (!units.find(u => u.value === unit)) {
				fetchSingleUnit(unit);
			}
		}
	}, [ unit, units ]);

	const unitTextByValue = useMemo(
		() => convertOptionsToTextByValue(units),
		[ units ]
	);
	const categoryOptions = useMemo(
		() => convertCategoriesToOptions(categories),
		[ categories ]
	);
	const categoryTextByValue = useMemo(
		() => convertOptionsToTextByValue(categoryOptions),
		[ categoryOptions ]
	);

	const classNames = crunchClasses({
		[styles.row]: true,
	});

	const handleIsFAChange = () => handleUpdateInternalLineItem(hash, { isFA: !isFA });

	const faAmount = useMemo(() => {
		if (isPresent(costShareAmount) && isFA) {
			return costShareAmount * (faRateAmount / 100);
		}

		return 0;
	}, [ costShareAmount, faRateAmount, isFA ]);

	const subtotalAmount = useMemo(
		() => toNumber(costShareAmount) + toNumber(faAmount),
		[ costShareAmount, faAmount ]
	);

	const onUnitFinderChange = unit => handleUpdateInternalLineItem(hash, { unit });

	return (
		<Table.Row key={hash} id={hash} className={classNames}>
			<EditableTableCell
				editable={isEditing}
				inputStyles={styles.periodDropdown}
				cellStyles={`${styles.cellAlignLeft} ${styles.cellWithDropdown}`}
				onChange={(e, { value }) => handleUpdateInternalLineItem(hash, { value })}
				value={value}
				textFormatter={text => categoryTextByValue[text]}
				renderInput={props => (
					<Dropdown
						{...props}
						selection
						options={categoryOptions}
					/>
				)}
			/>
			<EditableTableCell
				editable={isEditing}
				inputStyles={styles.periodDropdown}
				cellStyles={`${styles.cellAlignLeft} ${styles.cellWithDropdown}`}
				onChange={(e, { value }) => handleUpdateInternalLineItem(hash, { requirement: value })}
				value={requirement}
				renderInput={props => (
					<Dropdown
						{...props}
						selection
						options={REQUIREMENT_DROPDOWN_ITEMS}
					/>
				)}
			/>
			<EditableTableCell
				editable={isEditing}
				inputStyles={styles.orgFinderForm}
				cellStyles={`${styles.cellAlignLeft} ${styles.cellWithDropdown}`}
				onChange={onUnitFinderChange}
				value={unit}
				textFormatter={value => unitTextByValue?.[value]}
				renderInput={props => <UnitSearchInput selection {...props} />}
			/>
			<EditableTableCell
				editable={isEditing}
				inputStyles={styles.periodDropdown}
				cellStyles={styles.cellAlignLeft}
				onChange={(e, { value }) => handleUpdateInternalLineItem(hash, { type: value })}
				value={type}
				renderInput={props => (
					<Dropdown
						{...props}
						selection
						options={TYPE_DROPDOWN_OPTIONS}
					/>
				)}
			/>
			<EditableTableCell
				editable={isEditing}
				textFormatter={currencyFormatter}
				value={costShareAmount}
				cellStyles={`${styles.cellAlignRight} ${styles.valueCellPadding}`}
				onChange={event => handleUpdateInternalLineItem(hash, { costShareAmount: event.target.value })}
			/>
			<Table.Cell className={styles.cellAlignRight}>
				<Checkbox
					checked={isFA}
					disabled={!isEditing}
					onClick={handleIsFAChange}
				/>
			</Table.Cell>
			<Table.Cell className={`${styles.cellAlignRight} ${styles.valueCellPadding}`}>
				{isFA && (
					<span className={styles.cellAlignRight}>
						{formatNumberAsPercent(faRateAmount)}
					</span>
				)}
			</Table.Cell>
			<Table.Cell className={`${styles.cellAlignRight} ${styles.valueCellPadding}`}>
				<span className={styles.cellAlignRight}>
					{currencyFormatter(faAmount)}
				</span>
			</Table.Cell>
			<Table.Cell className={`${styles.cellAlignRight} ${styles.valueCellPadding}`}>
				<span className={styles.cellAlignRight}>
					{currencyFormatter(subtotalAmount)}
				</span>
			</Table.Cell>
			<Table.Cell className={styles.cellAlignCenter}>
				{isEditing && (
					<IconButton
						tertiary
						onClick={() => handleSyncRemoveItem(hash)}
						className={styles.deleteButton}
						icon="trash"
					/>
				)}
			</Table.Cell>
		</Table.Row>
	);
};

export default InternalCostShareRow;
